import { formatMutationError } from "./mutationError";

/** Stop bulk delete after this many consecutive failures (likely systemic). */
export const BULK_DELETE_CONSECUTIVE_FAIL_LIMIT = 3;

/**
 * Whether a delete mutation rejection means the issue is already gone.
 * Treat as success so bulk "Delete All" is idempotent under races/retries.
 */
export function isIssueAlreadyGoneError(err) {
  return err?.status === 404;
}

/**
 * Run per-issue deletes with progress, 404-as-success, and fail-fast on
 * consecutive systemic failures. `deleteOne(issueId)` should throw on failure
 * (e.g. RTK `.unwrap()`).
 */
export async function runBulkIssueDeletes({
  issues,
  getIssueId,
  deleteOne,
  onProgress,
  consecutiveFailLimit = BULK_DELETE_CONSECUTIVE_FAIL_LIMIT,
  delayMs = 100,
}) {
  let successCount = 0;
  let failedCount = 0;
  let consecutiveFailures = 0;
  const successfulIssueIds = [];
  const failureSamples = [];
  let abortedEarly = false;

  for (let i = 0; i < issues.length; i++) {
    const issue = issues[i];
    const issueId = getIssueId(issue);

    if (!issueId) {
      failedCount++;
      consecutiveFailures++;
      if (failureSamples.length < 3) {
        failureSamples.push("Missing issue id on selected row");
      }
    } else {
      try {
        await deleteOne(issueId);
        successCount++;
        successfulIssueIds.push(issueId);
        consecutiveFailures = 0;
      } catch (err) {
        if (isIssueAlreadyGoneError(err)) {
          successCount++;
          successfulIssueIds.push(issueId);
          consecutiveFailures = 0;
        } else {
          console.error(`Failed to delete issue ${issueId}:`, err);
          failedCount++;
          consecutiveFailures++;
          if (failureSamples.length < 3) {
            failureSamples.push(
              formatMutationError(err, `Failed to delete ${issueId}`)
            );
          }
        }
      }
    }

    const isLast = i + 1 === issues.length;
    const shouldAbort =
      consecutiveFailures >= consecutiveFailLimit && i + 1 < issues.length;

    if (onProgress) {
      onProgress({
        current: i + 1,
        total: issues.length,
        success: successCount,
        failed: failedCount,
        isComplete: isLast || shouldAbort,
      });
    }

    if (shouldAbort) {
      abortedEarly = true;
      const remaining = issues.length - (i + 1);
      failedCount += remaining;
      if (onProgress) {
        onProgress({
          current: issues.length,
          total: issues.length,
          success: successCount,
          failed: failedCount,
          isComplete: true,
        });
      }
      break;
    }

    if (!isLast && delayMs > 0) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }

  let errorMessage = null;
  if (failedCount > 0) {
    const sample =
      failureSamples.length > 0 ? ` First errors: ${failureSamples.join("; ")}` : "";
    const abortNote = abortedEarly
      ? ` Stopped early after ${consecutiveFailLimit} consecutive failures.`
      : "";
    errorMessage = `Completed with ${successCount} successful and ${failedCount} failed deletions.${abortNote}${sample}`;
  }

  return {
    successCount,
    failedCount,
    successfulIssueIds,
    abortedEarly,
    errorMessage,
  };
}
